import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecurityTab = () => {
  const [securitySettings, setSecuritySettings] = useState({
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
      passwordExpiry: 90,
      preventReuse: 5
    },
    sessionSettings: {
      sessionTimeout: 480,
      maxConcurrentSessions: 3,
      forceLogoutOnIdle: true,
      rememberMe: false
    },
    accessControl: {
      enableTwoFactor: false,
      allowedIpRanges: ['192.168.1.0/24', '10.0.0.0/8'],
      blockSuspiciousActivity: true,
      maxFailedAttempts: 5,
      lockoutDuration: 30
    },
    auditSettings: {
      enableAuditLog: true,
      logUserActions: true,
      logSystemEvents: true,
      logCallRecords: true,
      retentionPeriod: 365
    }
  });

  const [auditLogs, setAuditLogs] = useState([
    {
      id: 1,
      timestamp: '2025-11-07 15:45:23',
      user: 'ahmet.yilmaz',
      action: 'Kullanıcı Girişi',
      details: 'Başarılı giriş - IP: 192.168.1.100',
      severity: 'info',
      category: 'authentication'
    },
    {
      id: 2,
      timestamp: '2025-11-07 15:30:15',
      user: 'fatma.demir',
      action: 'Ayar Değişikliği',
      details: 'SIP sunucu ayarları güncellendi',
      severity: 'warning',
      category: 'configuration'
    },
    {
      id: 3,
      timestamp: '2025-11-07 15:15:08',
      user: 'system',
      action: 'Güvenlik Uyarısı',
      details: 'Şüpheli giriş denemesi - IP: 203.0.113.45',
      severity: 'error',
      category: 'security'
    },
    {
      id: 4,
      timestamp: '2025-11-07 14:50:42',
      user: 'mehmet.ozkan',
      action: 'Arama Kaydı',
      details: 'Giden arama: +905321234567',
      severity: 'info',
      category: 'call'
    }
  ]);

  const [activeTab, setActiveTab] = useState('password');
  const [showAddIpModal, setShowAddIpModal] = useState(false);
  const [newIpRange, setNewIpRange] = useState('');

  const sessionTimeoutOptions = [
    { value: 60, label: '1 saat' },
    { value: 240, label: '4 saat' },
    { value: 480, label: '8 saat' },
    { value: 720, label: '12 saat' },
    { value: 1440, label: '24 saat' }
  ];

  const retentionOptions = [
    { value: 30, label: '30 gün' },
    { value: 90, label: '90 gün' },
    { value: 180, label: '6 ay' },
    { value: 365, label: '1 yıl' },
    { value: 730, label: '2 yıl' }
  ];

  const handlePasswordPolicyChange = (field, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      passwordPolicy: {
        ...prev?.passwordPolicy,
        [field]: value
      }
    }));
  };

  const handleSessionSettingsChange = (field, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      sessionSettings: {
        ...prev?.sessionSettings,
        [field]: value
      }
    }));
  };

  const handleAccessControlChange = (field, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      accessControl: {
        ...prev?.accessControl,
        [field]: value
      }
    }));
  };

  const handleAuditSettingsChange = (field, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      auditSettings: {
        ...prev?.auditSettings,
        [field]: value
      }
    }));
  };

  const addIpRange = () => {
    if (newIpRange?.trim()) {
      setSecuritySettings(prev => ({
        ...prev,
        accessControl: {
          ...prev?.accessControl,
          allowedIpRanges: [...prev?.accessControl?.allowedIpRanges, newIpRange?.trim()]
        }
      }));
      setNewIpRange('');
      setShowAddIpModal(false);
    }
  };

  const removeIpRange = (index) => {
    setSecuritySettings(prev => ({
      ...prev,
      accessControl: {
        ...prev?.accessControl,
        allowedIpRanges: prev?.accessControl?.allowedIpRanges?.filter((_, i) => i !== index)
      }
    }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'bg-error/10 text-error border-error/20';
      case 'warning': return 'bg-warning/10 text-warning border-warning/20';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'authentication': return 'LogIn';
      case 'configuration': return 'Settings';
      case 'security': return 'Shield';
      case 'call': return 'Phone';
      default: return 'Info';
    }
  };

  const tabs = [
    { id: 'password', label: 'Şifre Politikası', icon: 'Lock' },
    { id: 'session', label: 'Oturum Ayarları', icon: 'Clock' },
    { id: 'access', label: 'Erişim Kontrolü', icon: 'Shield' },
    { id: 'audit', label: 'Denetim Günlükleri', icon: 'FileText' }
  ];

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Password Policy Tab */}
      {activeTab === 'password' && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Şifre Politikası</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Minimum Şifre Uzunluğu"
              type="number"
              value={securitySettings?.passwordPolicy?.minLength}
              onChange={(e) => handlePasswordPolicyChange('minLength', parseInt(e?.target?.value))}
              min="6"
              max="32"
            />
            
            <Input
              label="Şifre Geçerlilik Süresi (gün)"
              type="number"
              value={securitySettings?.passwordPolicy?.passwordExpiry}
              onChange={(e) => handlePasswordPolicyChange('passwordExpiry', parseInt(e?.target?.value))}
              description="0 = süresiz"
            />
            
            <Input
              label="Şifre Tekrar Kullanım Engeli"
              type="number"
              value={securitySettings?.passwordPolicy?.preventReuse}
              onChange={(e) => handlePasswordPolicyChange('preventReuse', parseInt(e?.target?.value))}
              description="Son kaç şifrenin tekrar kullanımı engellensin"
            />
          </div>

          <div className="mt-6">
            <h4 className="font-medium text-slate-900 mb-4">Şifre Gereksinimleri</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Checkbox
                label="Büyük Harf Zorunlu"
                description="En az bir büyük harf içermeli"
                checked={securitySettings?.passwordPolicy?.requireUppercase}
                onChange={(e) => handlePasswordPolicyChange('requireUppercase', e?.target?.checked)}
              />
              <Checkbox
                label="Küçük Harf Zorunlu"
                description="En az bir küçük harf içermeli"
                checked={securitySettings?.passwordPolicy?.requireLowercase}
                onChange={(e) => handlePasswordPolicyChange('requireLowercase', e?.target?.checked)}
              />
              <Checkbox
                label="Rakam Zorunlu"
                description="En az bir rakam içermeli"
                checked={securitySettings?.passwordPolicy?.requireNumbers}
                onChange={(e) => handlePasswordPolicyChange('requireNumbers', e?.target?.checked)}
              />
              <Checkbox
                label="Özel Karakter Zorunlu"
                description="En az bir özel karakter içermeli"
                checked={securitySettings?.passwordPolicy?.requireSpecialChars}
                onChange={(e) => handlePasswordPolicyChange('requireSpecialChars', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      )}
      {/* Session Settings Tab */}
      {activeTab === 'session' && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Oturum Ayarları</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Oturum Zaman Aşımı"
              options={sessionTimeoutOptions}
              value={securitySettings?.sessionSettings?.sessionTimeout}
              onChange={(value) => handleSessionSettingsChange('sessionTimeout', value)}
              description="Hareketsizlik durumunda oturum kapatma süresi"
            />
            
            <Input
              label="Maksimum Eşzamanlı Oturum"
              type="number"
              value={securitySettings?.sessionSettings?.maxConcurrentSessions}
              onChange={(e) => handleSessionSettingsChange('maxConcurrentSessions', parseInt(e?.target?.value))}
              min="1"
              max="10"
              description="Bir kullanıcının aynı anda açabileceği oturum sayısı"
            />
          </div>

          <div className="mt-6 space-y-4">
            <Checkbox
              label="Hareketsizlikte Otomatik Çıkış"
              description="Belirtilen süre hareketsiz kalındığında otomatik olarak çıkış yap"
              checked={securitySettings?.sessionSettings?.forceLogoutOnIdle}
              onChange={(e) => handleSessionSettingsChange('forceLogoutOnIdle', e?.target?.checked)}
            />
            <Checkbox
              label="Beni Hatırla Özelliği"
              description="Kullanıcıların 'Beni Hatırla' seçeneğini kullanmasına izin ver"
              checked={securitySettings?.sessionSettings?.rememberMe}
              onChange={(e) => handleSessionSettingsChange('rememberMe', e?.target?.checked)}
            />
          </div>
        </div>
      )}
      {/* Access Control Tab */}
      {activeTab === 'access' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Erişim Kontrolü</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Input
                label="Maksimum Başarısız Giriş Denemesi"
                type="number"
                value={securitySettings?.accessControl?.maxFailedAttempts}
                onChange={(e) => handleAccessControlChange('maxFailedAttempts', parseInt(e?.target?.value))}
                min="3"
                max="10"
              />
              
              <Input
                label="Hesap Kilitleme Süresi (dakika)"
                type="number"
                value={securitySettings?.accessControl?.lockoutDuration}
                onChange={(e) => handleAccessControlChange('lockoutDuration', parseInt(e?.target?.value))}
                min="5"
                max="1440"
              />
            </div>

            <div className="space-y-4">
              <Checkbox
                label="İki Faktörlü Kimlik Doğrulama"
                description="Kullanıcılar için 2FA zorunlu kıl"
                checked={securitySettings?.accessControl?.enableTwoFactor}
                onChange={(e) => handleAccessControlChange('enableTwoFactor', e?.target?.checked)}
              />
              <Checkbox
                label="Şüpheli Aktivite Engelleme"
                description="Anormal giriş denemelerini otomatik olarak engelle"
                checked={securitySettings?.accessControl?.blockSuspiciousActivity}
                onChange={(e) => handleAccessControlChange('blockSuspiciousActivity', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-slate-900">İzin Verilen IP Aralıkları</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddIpModal(true)}
                iconName="Plus"
                iconPosition="left"
              >
                IP Ekle
              </Button>
            </div>
            
            <div className="space-y-2">
              {securitySettings?.accessControl?.allowedIpRanges?.map((ipRange, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-mono text-sm text-slate-900">{ipRange}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeIpRange(index)}
                    iconName="Trash2"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Audit Logs Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Denetim Ayarları</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Select
                label="Günlük Saklama Süresi"
                options={retentionOptions}
                value={securitySettings?.auditSettings?.retentionPeriod}
                onChange={(value) => handleAuditSettingsChange('retentionPeriod', value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Checkbox
                label="Denetim Günlüğü Etkin"
                description="Sistem olaylarını kaydet"
                checked={securitySettings?.auditSettings?.enableAuditLog}
                onChange={(e) => handleAuditSettingsChange('enableAuditLog', e?.target?.checked)}
              />
              <Checkbox
                label="Kullanıcı İşlemlerini Kaydet"
                description="Kullanıcı eylemlerini günlükle"
                checked={securitySettings?.auditSettings?.logUserActions}
                onChange={(e) => handleAuditSettingsChange('logUserActions', e?.target?.checked)}
              />
              <Checkbox
                label="Sistem Olaylarını Kaydet"
                description="Sistem seviyesi olayları günlükle"
                checked={securitySettings?.auditSettings?.logSystemEvents}
                onChange={(e) => handleAuditSettingsChange('logSystemEvents', e?.target?.checked)}
              />
              <Checkbox
                label="Arama Kayıtlarını Günlükle"
                description="Tüm arama aktivitelerini kaydet"
                checked={securitySettings?.auditSettings?.logCallRecords}
                onChange={(e) => handleAuditSettingsChange('logCallRecords', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <h4 className="font-medium text-slate-900 mb-4">Son Denetim Günlükleri</h4>
            
            <div className="space-y-3">
              {auditLogs?.map((log) => (
                <div key={log?.id} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={getCategoryIcon(log?.category)} size={16} className="text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-slate-900">{log?.action}</span>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(log?.severity)}`}>
                        {log?.severity}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-1">{log?.details}</p>
                    <div className="flex items-center space-x-4 text-xs text-slate-500">
                      <span>{log?.timestamp}</span>
                      <span>Kullanıcı: {log?.user}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 text-center">
              <Button variant="outline" size="sm">
                Tüm Günlükleri Görüntüle
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Add IP Range Modal */}
      {showAddIpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">IP Aralığı Ekle</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddIpModal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6">
              <Input
                label="IP Adresi veya CIDR Aralığı"
                type="text"
                value={newIpRange}
                onChange={(e) => setNewIpRange(e?.target?.value)}
                placeholder="192.168.1.0/24 veya 10.0.0.1"
                description="Tek IP adresi veya CIDR notasyonu kullanabilirsiniz"
              />
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowAddIpModal(false)}
              >
                İptal
              </Button>
              <Button
                variant="default"
                onClick={addIpRange}
                iconName="Plus"
                iconPosition="left"
              >
                Ekle
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-200">
        <Button variant="outline">
          Varsayılana Sıfırla
        </Button>
        <Button
          variant="default"
          iconName="Save"
          iconPosition="left"
        >
          Güvenlik Ayarlarını Kaydet
        </Button>
      </div>
    </div>
  );
};

export default SecurityTab;